/* =========================================================================
   COOKEHOUSE: ch-deferred-embed.js (Aug 2026)

   Defers a third-party embed until it is actually needed.

   WHY THIS EXISTS
   ---------------
   Two homepage blocks each embed the same Jotform newsletter form inside a
   hidden modal. Because the embed markup sat directly in the DOM, both forms
   booted a full Jotform runtime on every page load - 38 requests, 566 KB over
   the wire and ~10.5 MB uncompressed (7.4 MB of it JavaScript), for two email
   fields nobody had opened yet. That was 48% of the homepage's total parse
   budget. See the Aug 2026 performance audit.

   HOW IT WORKS
   ------------
   The embed markup is parked inside a <template>. Template content is inert:
   the browser will not fetch the iframe, run the scripts, or load any assets
   inside it. On first open we clone it, re-create the <script> elements (a
   cloned script never executes) and inject.

   Ordering matters. Jotform ships an external handler script followed by an
   inline call to window.jotformEmbedHandler(). During normal parsing those run
   in order; when injected they would not, and the inline call would throw. So
   external scripts are awaited before the next script in the sequence runs.

   External scripts are deduplicated by src across every instance on the page,
   so opening both modals fetches the handler once and calls it twice - which
   is exactly what you want.

   USAGE
   -----
   Manual (modals - caller decides when):

     <ch-deferred-embed>
       <template>...embed markup...</template>
     </ch-deferred-embed>

     el.warm();   // optional - preconnect only, safe to call on hover
     el.load();   // idempotent, returns a Promise

   Self-triggering on approach (below-the-fold widgets):

     <ch-deferred-embed data-load-on="visible" data-root-margin="600px">
       <template>...embed markup...</template>
     </ch-deferred-embed>

   With data-load-on="visible" the element warms its connections at 1200px
   from the viewport and loads at data-root-margin (default 600px), so the
   widget is populated before the user reaches it. Without that attribute the
   element does nothing on its own - that is what makes it safe inside hidden
   modals, which must never load until opened.

   Falls back to loading immediately where IntersectionObserver is missing:
   better to load early than never.

   Idempotent by design: safe to load twice, safe to define twice, safe to
   re-run on shopify:section:load.
   ========================================================================= */

(function () {
  'use strict';

  if (window.customElements && customElements.get('ch-deferred-embed')) return;

  /* src -> Promise. Module scope, so it is shared by every instance. */
  var externalScripts = new Map();

  /* Origins already preconnected, so we never emit a duplicate <link>. */
  var warmedOrigins = new Set();

  function preconnect(origin) {
    if (!origin || warmedOrigins.has(origin)) return;
    warmedOrigins.add(origin);
    ['preconnect', 'dns-prefetch'].forEach(function (rel) {
      var link = document.createElement('link');
      link.rel = rel;
      link.href = origin;
      if (rel === 'preconnect') link.crossOrigin = '';
      document.head.appendChild(link);
    });
  }

  function originOf(url) {
    try {
      return new URL(url, window.location.href).origin;
    } catch (e) {
      return null;
    }
  }

  function copyAttributes(from, to) {
    for (var i = 0; i < from.attributes.length; i++) {
      var attr = from.attributes[i];
      try {
        to.setAttribute(attr.name, attr.value);
      } catch (e) {
        /* Ignore attributes the browser refuses - never block the embed. */
      }
    }
  }

  function loadExternalScript(sourceScript) {
    var src = sourceScript.src;
    if (externalScripts.has(src)) return externalScripts.get(src);

    var promise = new Promise(function (resolve) {
      var script = document.createElement('script');
      copyAttributes(sourceScript, script);
      script.src = src;
      /* Resolve on error too. A dead third party must not stall the chain. */
      script.onload = function () { resolve(true); };
      script.onerror = function () {
        if (window.console) console.warn('[ch-deferred-embed] failed to load', src);
        resolve(false);
      };
      document.head.appendChild(script);
    });

    externalScripts.set(src, promise);
    return promise;
  }

  function runInlineScript(sourceScript, mount) {
    var script = document.createElement('script');
    copyAttributes(sourceScript, script);
    script.text = sourceScript.textContent || '';
    mount.appendChild(script);
  }

  class ChDeferredEmbed extends HTMLElement {
    /* Doing nothing on connect is the default, and it is the whole point -
       an embed inside a hidden modal must not load until the modal opens.
       Only data-load-on="visible" opts into self-triggering. */
    connectedCallback() {
      if (this.dataset.loadOn !== 'visible') return;
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.stopObserving();
    }

    stopObserving() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._warmIo) { this._warmIo.disconnect(); this._warmIo = null; }
    }

    observeVisibility() {
      if (this._io || this._loading) return;

      /* No IntersectionObserver: load now rather than never. */
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      const self = this;

      /* Warm sockets well ahead of the load point - preconnect only. */
      this._warmIo = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        self.warm();
        if (self._warmIo) { self._warmIo.disconnect(); self._warmIo = null; }
      }, { rootMargin: '1200px' });
      this._warmIo.observe(this);

      this._io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        self.stopObserving();
        self.load();
      }, { rootMargin: this.dataset.rootMargin || '600px' });
      this._io.observe(this);
    }

    /* Open the connection early without downloading anything. Cheap enough to
       call on hover or focus; it costs a socket, not a payload. */
    warm() {
      if (this._warmed) return;
      this._warmed = true;

      const template = this.querySelector('template');
      if (!template) return;

      const nodes = template.content.querySelectorAll('script[src], iframe[src]');
      Array.prototype.forEach.call(nodes, function (node) {
        preconnect(originOf(node.getAttribute('src')));
      });
    }

    load() {
      if (this._loading) return this._loading;

      const template = this.querySelector('template');
      if (!template) {
        this._loading = Promise.resolve();
        return this._loading;
      }

      this.setAttribute('data-loaded', '');

      const fragment = template.content.cloneNode(true);

      /* Pull the scripts out before injecting - we re-create them below, in
         order. Cloned <script> nodes never execute, so this is not optional. */
      const scripts = Array.prototype.slice.call(fragment.querySelectorAll('script'));
      scripts.forEach(function (script) {
        if (script.parentNode) script.parentNode.removeChild(script);
      });

      const mount = document.createElement('div');
      mount.className = 'ch-deferred-embed__mount';
      mount.appendChild(fragment);
      this.appendChild(mount);

      /* Sequence the scripts: each external one is awaited so that any inline
         script depending on it (jotformEmbedHandler) has its global ready. */
      let chain = Promise.resolve();
      scripts.forEach(function (script) {
        chain = chain.then(function () {
          if (script.src) return loadExternalScript(script);
          return runInlineScript(script, mount);
        });
      });

      const self = this;
      this._loading = chain.then(function () {
        self.setAttribute('data-ready', '');
        self.dispatchEvent(new CustomEvent('ch:embed-ready', { bubbles: true }));
      });

      return this._loading;
    }
  }

  customElements.define('ch-deferred-embed', ChDeferredEmbed);
})();
